import React, { type ChangeEvent, useEffect, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import PageHeader from '../../global/components/PageHeader';
import { apiGetStudentsFromAis } from '@/modules/students/services/student.service';
import { toast } from 'react-toastify';
import { StudentsByIinStepInput } from '@/modules/students/components/StudentsByIin/StudentsByIinStepInput';
import { StudentsByIinStepClosed } from '@/modules/students/components/StudentsByIin/StudentsByIinStepClosed';
import {
    apiCreateQuickTransaction,
    apiTransactionFreedomWebhook
} from '@/modules/transactions/services/transactions.services';
import {
    StudentsByIinStepStartTransaction
} from '@/modules/students/components/StudentsByIin/StudentsByIinStepStartTransaction';
import { StudentsByIinStepOpened } from '@/modules/students/components/StudentsByIin/StudentsByIinStepOpened';
import { PictureAsPdf } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { BACKEND_URL } from '@/global/actions/apiVariables';
import { useSearchParams } from 'react-router-dom';
import type { TransactionCreateDtoPaymentSystemEnum } from '@/global/api/gen/ts/TransactionCreateDto';
import { StudentsByIinTransaction } from '@/modules/students/components/StudentsByIin/StudentsByIinTransaction';
import { openPage } from '@/global/helpers/openPage';

type Steps = 'input' | 'closed' | 'transaction' | 'startTransaction' | 'opened';

function FeePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const iinSP = searchParams.get('iin');

    const [agreed, setAgreed] = useState<boolean>(false);
    const [iin, setIin] = useState<string>(iinSP ?? '');

    const [step, setStep] = useState<Steps>('input');
    const [payment, setPayment] = useState(false);
    const [phone, setPhone] = useState<{ phone: string, isValid: boolean }>({phone: '', isValid: false});
    const [email, setEmail] = useState<{ email: string, isValid: boolean }>({email: '', isValid: false});
    const [selectedTariff, setSelectedTariff] = useState<'tariff1' | 'tariff2' | 'tariff3' | 'tariff4' | null>(null);

    const {mutate, data} = apiCreateQuickTransaction();

    useEffect(() => {
        const handleIinSP = async () => {
            await handleFetch();
            searchParams.delete('iin');
            setSearchParams(searchParams, {
                replace: true
            });
        };
        if (iinSP) {
            handleIinSP();
        }
    }, [iinSP]);

    const {data: studentsByIIN, isLoading: studentsByIINLoading, refetch} = apiGetStudentsFromAis(iin, {
        enabled: false,
        retry: false
    });
    const {data: dataTransaction} = apiTransactionFreedomWebhook(data?.invoice_id, payment);
    const handleIinChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const sendIin = e.currentTarget.value.replace(/\D/g, '');
        if (sendIin.length > 12) {
            return;
        }
        setIin(sendIin);
    };

    useEffect(() => {
        if (dataTransaction && dataTransaction.status === 2) {
            setPayment(true);
            setStep('opened');
            toast.success('Транзакция проведена');
            void refetch();
            return;
        }
        if (dataTransaction && (dataTransaction.status === 3 || dataTransaction.status === 10)) {
            setStep('input');
            toast.error('Ошибка транзакции. Повторите позже.');
            return;
        }
    }, [dataTransaction]);

    const handleFetch = async () => {
        const res = await refetch();
        if (res.isError) {
            toast.error('Неизвестная ошибка.');
            return;
        }
        if (res.data && !res.data.obfuscate) {
            setStep('opened');
            return;
        }
        setStep('closed');
    };

    const handleFetchStudents = async () => {
        if (!agreed) {
            toast.error('Необходимо подтвердить согласие лица, в отношении которого ведется поиск учебных данных');
            return;
        }
        if (iin.length !== 12) {
            toast.error('Неверно введен ИИН');
            return;
        }
        await handleFetch();
    };

    const handleStartTransaction = (paymentMethod: TransactionCreateDtoPaymentSystemEnum) => {
        if (!email.isValid) {
            toast.error('Введите электронную почту для отправки данных');
            return;
        }
        if (!phone.isValid) {
            toast.error('Введите номер телефона');
            return;
        }

        if (!selectedTariff) {
            toast.error('Выберите тариф');
            return;
        }

        mutate({
            amount: 1,
            type: 'payment_student_info',
            payment_system: paymentMethod,
            iin,
            extra_data: {
                phone: phone.phone,
                email: email.email,
                iin,
                tariff: selectedTariff
            }
        }, {
            onSuccess: (data) => {
                if (data.payment_system === 'kaspi' && data.payment_url) {
                    openPage(data.payment_url);
                }
                if (data.payment_system === 'kaspi' && !data.payment_url) {
                    toast.error('Ошибка при создание ссылки для оплаты в Kaspi.kz. Повторить позже');
                    returnFirstStep();
                    return;
                }
                setStep('transaction');
            }
        });
    };

    const nextStepForClosed = () => {
        setStep('startTransaction');
    };

    const returnFirstStep = () => {
        setStep('input');
        setIin('');
        setPayment(false);
    };

    return (
        <Box
            className="App"
            sx={{height: '100%', display: 'flex', flexDirection: 'column'}}
        >
            <PageHeader/>

            <Stack
                width={{xs: '90%', lg: '80%'}}
                alignItems={'center'}
                justifyContent={'center'}
                gap={4}
                margin={'0 auto'}
                marginTop={4}
            >
                <Typography
                    sx={{
                        textAlign: 'center',
                        fontSize: {xs: '30px', md: '37px'}
                    }}
                >
                    Проверка данных об обучении
                </Typography>
                {
                    step === 'input' && (
                        <StudentsByIinStepInput
                            iin={iin}
                            handleIinChange={handleIinChange}
                            agreed={agreed}
                            setAgreed={setAgreed}
                            handleFetchStudents={handleFetchStudents}
                            studentsByIINLoading={studentsByIINLoading}
                        />
                    )
                }
                {
                    step === 'closed' && (
                        studentsByIIN ? (
                                <StudentsByIinStepClosed
                                    nextStep={nextStepForClosed}
                                    returnFirstStep={returnFirstStep}
                                    studentsByIIN={studentsByIIN}
                                />
                            )
                            :
                            <CircularProgress/>
                    )
                }
                {
                    step === 'startTransaction' && studentsByIIN && (
                        <StudentsByIinStepStartTransaction
                            returnFirstStep={returnFirstStep}
                            studentInfo={studentsByIIN.students[0]}
                            handleStartTransaction={handleStartTransaction}
                            setPhone={setPhone}
                            setEmail={setEmail}
                            selectedTariff={selectedTariff}
                            setSelectedTariff={setSelectedTariff}
                            studentsByIIN={studentsByIIN}
                        />
                    )
                }
                {
                    step === 'transaction' && data && (
                        <StudentsByIinTransaction
                            returnFirstStep={returnFirstStep}
                            data={data}
                        />
                    )
                }
                {
                    step === 'opened' && (
                        studentsByIIN ? (
                                <>
                                    <StudentsByIinStepOpened
                                        studentsByIIN={studentsByIIN}
                                        returnFirstStep={returnFirstStep}
                                    />
                                    {!!studentsByIIN?.pddtest_promocode && <Stack
                                        gap={2}
                                        sx={{
                                            maxWidth: 700,
                                            padding: 4,
                                            borderRadius: '12px',
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        <Typography fontWeight={'lighter'} textAlign="center">
                                            Ваш код для обучения на сайте pddtest.kz:
                                            <br/>
                                            <b>
                                                {studentsByIIN?.pddtest_promocode}
                                            </b>
                                            <Typography fontWeight={'lighter'} sx={{mt: 2}}>Для активации и получения доступа к
                                                курсам
                                                зарегистрируйтесь на
                                                сайте <a
                                                    href="https://pddtest.kz"
                                                    target="_blank"
                                                    style={{color: 'blue'}}>pddtest.kz</a> и активируйте его через кнопку
                                                "Активировать подарочный сертификат", либо активируйте как промокод в
                                                корзине</Typography>
                                        </Typography>
                                    </Stack>}
                                    <Stack
                                        gap={2}
                                        sx={{
                                            padding: 4,
                                            borderRadius: '12px',
                                            backgroundColor: 'white'
                                        }}
                                    >
                                        <LoadingButton
                                            href={`${BACKEND_URL}/pdf/student-info/${iin}/?transaction_id=${data?.id}`}
                                            target={'_blank'}
                                            variant={'contained'}
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                paddingBlock: 4
                                            }}
                                        >
                                            <PictureAsPdf/>
                                            Скачать справку об обучении
                                        </LoadingButton>
                                        <Typography fontWeight={'lighter'}>
                                            Также все данные были отправлены на почту
                                            {' '}
                                            <b>
                                                {studentsByIIN.email}
                                            </b>
                                        </Typography>
                                    </Stack>
                                </>
                            )
                            :
                            <CircularProgress/>
                    )
                }
            </Stack>
        </Box>
    );
}

export default FeePage;
